import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../api/errors';
import loginBackgroundUrl from '../assets/nen-dang-nhap.webp';
import { useAuth } from '../auth/useAuth';
import { BrandMark } from '../layout/BrandMark';
import { BrandColors } from '../theme/brand';

/** Giá trị của biểu mẫu đăng nhập. */
interface LoginFormValues {
  readonly userName: string;
  readonly password: string;
}

/**
 * Trang đăng nhập.
 *
 * Cố ý giữ đơn giản: một biểu mẫu hai ô, không có phần "ghi nhớ đăng nhập" — việc ghi nhớ đã do
 * cookie refresh token đảm nhiệm, và thêm một ô tích nữa chỉ làm người dùng tưởng mình đang
 * chọn giữa hai chế độ khác nhau.
 */
export function LoginPage(): ReactNode {
  const { signIn } = useAuth();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(values: LoginFormValues): Promise<void> {
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      await signIn(values.userName, values.password);
    } catch (error) {
      // Hiển thị đúng thông báo từ máy chủ. Máy chủ cố tình trả về cùng một câu cho cả trường hợp
      // sai tên đăng nhập và sai mật khẩu, nên giao diện không được đoán thêm để "giúp" người dùng.
      setErrorMessage(
        error instanceof ApiError
          ? error.message
          : 'Không kết nối được tới máy chủ. Kiểm tra lại đường mạng.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    // Ảnh nền: dây chuyền tự động hóa MVA Lab, bản gốc ở docs/thuong-hieu/nen-dang-nhap-goc.jpg.
    <div className="erp-login erp-login--photo" style={{ backgroundImage: `url(${loginBackgroundUrl})` }}>
      {/*
        Khung giới thiệu: nền xanh dương nhận diện, chữ trắng, một vạch gold nhỏ; logo nằm trong ô
        trắng vì logo chính thức có nền trắng và không được đảo màu. Chỉ ghi những gì
        có thật — tên hệ thống, các phần nó quản lý, và bốn lĩnh vực trong định hướng thương hiệu —
        không thêm khẩu hiệu tự đặt. Màn hình hẹp thì ẩn khung này đi, chỉ còn biểu mẫu.
      */}
      <aside className="erp-login-hero">
        <div className="erp-login-brand-plate">
          <BrandMark width={220} />
        </div>

        <div>
          <div className="erp-login-rule" />
          <h1>MVA Lab ERP</h1>
          <p>Quản lý dự án, khách hàng, cơ hội bán hàng và nội dung website của MVA Lab trên một hệ thống.</p>
          <div className="erp-login-keywords">
            <span>AI Computer</span>
            <span>Machine Vision</span>
            <span>Industrial Image Processing</span>
            <span>PC Control</span>
          </div>
        </div>

        <div className="erp-login-footnote">© {new Date().getFullYear()} MVA Lab</div>
      </aside>

      <main className="erp-login-main">
        <div className="erp-login-form">
          <div className="erp-login-form-brand">
            <BrandMark width={220} />
          </div>

          <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 6, fontWeight: 700 }}>
            Đăng nhập
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 28 }}>
            Đăng nhập để tiếp tục
          </Typography.Paragraph>

          {errorMessage !== null && (
            <Alert
              type="error"
              message={errorMessage}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form<LoginFormValues>
            layout="vertical"
            size="large"
            onFinish={(values) => {
              void handleSubmit(values);
            }}
            requiredMark={false}
            disabled={isSubmitting}
          >
            <Form.Item
              name="userName"
              label="Tên đăng nhập"
              rules={[{ required: true, message: 'Chưa nhập tên đăng nhập.' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: BrandColors.inkMuted }} />}
                placeholder="nguyen.van.a"
                autoComplete="username"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Mật khẩu"
              rules={[{ required: true, message: 'Chưa nhập mật khẩu.' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: BrandColors.inkMuted }} />}
                placeholder="Mật khẩu"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
              <Button type="primary" htmlType="submit" block loading={isSubmitting}>
                Đăng nhập
              </Button>
            </Form.Item>
          </Form>
        </div>
      </main>
    </div>
  );
}
